import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import InterventionBookingsSearchView from 'components/patient-entry/bookings-table/InterventionBookingsSearchView';
import InterventionBookingsTableView from 'components/patient-entry/bookings-table/InterventionBookingsTableView';
import {
  PatientCentralConsumerRenderProps,
  withPatientCentralContext
} from 'components/patient-entry/PatientCentralContext';
import PatientCentralPageHeader from 'components/patient-entry/PatientCentralPageHeader';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Dimmer, Loader } from 'semantic-ui-react';
import {
  clearTestResult,
  sendVaccinationInvitationEmailsForBookings,
  sendVaccinationReminderEmailsForBookings,
  verifyTestResultsForInterventionBookings
} from 'services/interventionBookingServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import {
  InterventionBookingInputChannel,
  InterventionBookingStatus,
  InterventionBookingTestResult
} from 'ts-types/api.enums';
import {
  InterventionBookingSearchResult,
  InterventionTestBookingSearchResult,
  InterventionVaccinationBookingSearchResult,
  VerifyTestResultsInterventionResponse
} from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const MainContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .page-header.page-header-ps {
    padding-top: 0;
    padding-bottom: 0.875rem !important;
  }

  .page-header .title {
    padding-left: 0.287rem;
    font-size: 1.1rem !important;
    color: #444444;
  }
`;

const StyledPraxSegment = styled(PraxStyledSegment)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
  padding: 0 !important;
  margin: 0 !important;
`;

export type InterventionBookings = InterventionBookingSearchResult[]
    | InterventionTestBookingSearchResult[]
    | InterventionVaccinationBookingSearchResult[];
export type InterventionBookingSearchRow = InterventionBookingSearchResult
    | InterventionTestBookingSearchResult
    | InterventionVaccinationBookingSearchResult;

export const isBookingSelectable = (booking: InterventionBookingSearchRow, status: InterventionBookingStatus) => {
  return booking.status === status
      && booking.inputChannel === InterventionBookingInputChannel.MIPOCO;
};

export const isTestInterventionSelectable = (booking: InterventionTestBookingSearchResult, selectTests: boolean) => {
  return selectTests && (
      !booking.testResultVerified
      && booking.status === InterventionBookingStatus.COMPLETED
      && booking.testResult === InterventionBookingTestResult.NEGATIVE
  );
}

interface Props extends PatientCentralConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    AuthConsumerRenderProps,
    MandateConsumerRenderProps,
    RouterProps,
    MessageSourceProps,
    NotificationSystemProps {
}

interface State {
  bookingsKey: number;
  bookings: InterventionBookings;
  selectedInterventionBookings: Set<number>;
  selectedTestInterventionBookings: Set<number>;

  pageActionInProgress: boolean;
  errorMessage?: string;
  verifyTestsResponse?: VerifyTestResultsInterventionResponse;
  cancelTokenSource: CancelTokenSource;
}

class PatientEntryView extends Component<Props, State> {

  private mounted: boolean = false;

  private updateMouseNotMovedTimeOut: number | undefined;
  private mouseNotMovedUpdateTimeOut = 180000;

  constructor(props: Props) {
    super(props);

    const {getAllMandates, getAllMandatesWithDeleted} = this.props;

    getAllMandates();
    getAllMandatesWithDeleted();

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      bookingsKey: 0,
      bookings: [],
      selectedInterventionBookings: new Set(),
      selectedTestInterventionBookings: new Set(),

      pageActionInProgress: false,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    if (this.updateMouseNotMovedTimeOut) {
      clearTimeout(this.updateMouseNotMovedTimeOut);
    }

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  onFilterBookings = (bookings: InterventionBookings) => {
    this.setState({
      bookingsKey: (this.state.bookingsKey + 1),
      bookings,
      selectedInterventionBookings: new Set(),
      selectedTestInterventionBookings: new Set()
    })
  };

  goToAddBooking = (parentBookingId?: number) => {
    const {history} = this.props;
    this.props.setParentBookingId(parentBookingId ? parentBookingId : undefined);
    history.push("/patient-central/onboard");
  };

  goToPoolTestkits = () => {
    const {history} = this.props;
    history.push("/patient-central/pool-testkit-registration");
  };

  goToEditBooking = (id: number) => {
    const {history} = this.props;
    this.props.setParentBookingId(undefined);
    history.push(`/patient-central/onboard/${id}`);
  };

  onInvoiceBookingDone = async (bookingId: number) => {
    await this.updateBookingFromServer(bookingId);
  };

  onUpdateBookingStatus = (bookingId?: number, status?: InterventionBookingStatus) => {
    this.props.searchBookings(this.onFilterBookings);
  };

  onUpdateBookingTestResult = async (bookingId: number) => {
    await this.updateBookingFromServer(bookingId);
  };

  updateBookingFromServer = async (bookingId: number) => {

    const updatedTest = await this.props.searchTestBookingById(bookingId);
    if (!updatedTest) {
      return;
    }

    const {bookings} = this.state;
    const newBookings = (bookings as InterventionTestBookingSearchResult[]).map((booking) => {
      return booking.bookingId !== bookingId
          ? booking
          : updatedTest;
    });
    this.setState({
      bookings: newBookings
    });
  };

  onClearBookingTestResult = async (bookingId: number) => {
    await clearTestResult(bookingId, this.state.cancelTokenSource);
    await this.updateBookingFromServer(bookingId);
  };

  onUpdateBookingSelection = (bookingId: number, selected: boolean, isTestSearch: boolean) => {
    const {selectedInterventionBookings, selectedTestInterventionBookings} = this.state;
    if (isTestSearch) {
      if (selected) {
        selectedTestInterventionBookings.add(bookingId);
      } else {
        selectedTestInterventionBookings.delete(bookingId);
      }
    } else {
      if (selected) {
        selectedInterventionBookings.add(bookingId);
      } else {
        selectedInterventionBookings.delete(bookingId);
      }
    }

    this.setState({
      selectedInterventionBookings: selectedInterventionBookings,
      selectedTestInterventionBookings: selectedTestInterventionBookings
    })
  };

  onUpdateBookings = (bookingIds: Set<number>, isTestSearch: boolean) => {
    if (isTestSearch) {
      this.setState({
        selectedTestInterventionBookings: bookingIds
      })
    } else {
      this.setState({
        selectedInterventionBookings: bookingIds
      })
    }
  };

  sendVaccinationInvitations = async () => {
    const {selectedInterventionBookings, cancelTokenSource} = this.state;
    const request = {bookingIds: Array.from(selectedInterventionBookings)};

    this.setState({
      pageActionInProgress: true
    });

    try {
      this.props.selectVaccinationStatus === InterventionBookingStatus.REQUESTED
          ? await sendVaccinationInvitationEmailsForBookings(request, cancelTokenSource)
          : await sendVaccinationReminderEmailsForBookings(request, cancelTokenSource);
      this.props.searchBookings(this.onFilterBookings);
    } catch (e) {
      // TODO vg 2021-05-26: show error message to the user
      // this.handleError(error.response.data)
    } finally {
      this.setState({
        pageActionInProgress: false
      });
    }
  };

  verifyTestResults = async () => {
    const {selectedTestInterventionBookings, cancelTokenSource} = this.state;
    const request = {bookingIds: Array.from(selectedTestInterventionBookings)};

    this.setState({
      pageActionInProgress: true
    });

    try {
      await verifyTestResultsForInterventionBookings(request, this.props.options.currentTreater.id, cancelTokenSource)
          .then(response => {
            this.setState({
              verifyTestsResponse: response
            });
            this.verificationDone(response.successfullyVerifiedTests);
            this.props.searchBookings(this.onFilterBookings);
          });
    } catch (e) {
      this.handleError(e.response.data)
    } finally {
      this.setState({
        pageActionInProgress: false
      });
    }
  };

  onChangeSelectVaccinationStatus = (value: InterventionBookingStatus) => {
    this.props.setSelectVaccinationStatus(value);
    this.setState({
      selectedInterventionBookings: new Set()
    });
  };

  onChangeSelectTestVerification = (value: boolean) => {
    this.props.setSelectVerifyTestResults(value);
    this.setState({
      selectedTestInterventionBookings: new Set()
    });
  };

  handleCursor = debounce((e: any) => {

    clearTimeout(this.updateMouseNotMovedTimeOut);

    this.updateMouseNotMovedTimeOut = setTimeout(() => {
      if (!this.props.anonymousMode) {
        this.props.setAnonymousMode();
      }
    }, this.mouseNotMovedUpdateTimeOut)
  }, 5000);

  handleError(error: any) {
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.showErrorMessage(`error.${violation.errorCode}`)
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.showErrorMessage(`error.${errorCode}`)
      } else {
        this.showErrorMessage('error.general');
      }
    }
  }

  showErrorMessage = (errorMessage: string) => {
    const {getMessage, selectionNotificationSystem} = this.props;
    //@ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      notification.addNotification({
        title: getMessage("error.header"),
        message: getMessage(errorMessage),
        level: 'error'
      });
    }
  }

  verificationDone = (numberOfVerifiedTests: number) => {

    const {getMessage, getMessageWithNamedParams, selectionNotificationSystem} = this.props;
    const {verifyTestsResponse} = this.state;
    const verificationErrors = verifyTestsResponse
        ? verifyTestsResponse.errorItems
            ? verifyTestsResponse.errorItems.length : 0
        : 0;

    //@ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      if (numberOfVerifiedTests > 0 && verificationErrors == 0) {
        notification.addNotification({
          title: getMessage("patientCentral.overview.successfullyVerifiedTests.title"),
          message: getMessageWithNamedParams(
              "patientCentral.overview.successfullyVerifiedTests.message",
              {numberOfVerifiedTests: numberOfVerifiedTests}),
          level: 'success'
        });
      } else if (numberOfVerifiedTests > 0 && verificationErrors > 0) {
        notification.addNotification({
          title: getMessage("patientCentral.overview.partialSuccessfullyVerifiedTests.title"),
          message: getMessageWithNamedParams(
              "patientCentral.overview.verificationOfSomeTestsFailed",
              {numberOfVerifiedTests: numberOfVerifiedTests, numberOfNotVerifiedTests: verificationErrors}),
          level: 'warning'
        });
      } else {
        notification.addNotification({
          title: getMessage("error.header"),
          message: getMessage("error.verifyTests.verificationFailed"),
          level: 'error'
        });
      }
    }
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {pageActionInProgress} = this.state;

    if (!this.props.referenceDataFetched) {
      return <></>;
    }
    return (
        <MainContentDiv
            onMouseMove={this.handleCursor}
        >
          <PatientCentralPageHeader
              className={"page-header-ps"}
              headerMessage={getMessage("patientCentral.home.title.main")}
          />

          <StyledPraxSegment id="patientCentralMainContent">
            <Dimmer active={pageActionInProgress} inverted>
              <Loader size="medium">{getMessage("patientCentral.info.actionInProgress")}</Loader>
            </Dimmer>

            <InterventionBookingsSearchView
                onOnboardPatient={this.goToAddBooking}
                onPoolTestkits={this.goToPoolTestkits}
                onFilterBookings={this.onFilterBookings}
                sendVaccinationInvitations={this.sendVaccinationInvitations}
                verifyTestResults={this.verifyTestResults}
                bookings={this.state.bookings}
                selectedInterventionBookings={this.state.selectedInterventionBookings}
                selectedTestInterventionBookings={this.state.selectedTestInterventionBookings}
                onChangeSelectVaccinationStatus={this.onChangeSelectVaccinationStatus}
                onChangeSelectTestVerification={this.onChangeSelectTestVerification}
            />
            <InterventionBookingsTableView
                key={this.state.bookingsKey}
                bookings={this.state.bookings}
                selectedInterventionBookings={this.state.selectedInterventionBookings}
                selectedTestInterventionBookings={this.state.selectedTestInterventionBookings}
                bookingsLoading={this.props.bookingsLoading}
                verifyTestResultsResponse={this.state.verifyTestsResponse}
                onEditBooking={this.goToEditBooking}
                onAddNewChildBooking={this.goToAddBooking}
                onInvoiceBookingDone={this.onInvoiceBookingDone}
                onUpdateBookingStatus={this.onUpdateBookingStatus}
                onUpdateBookingTestResult={this.onUpdateBookingTestResult}
                onClearBookingTestResult={this.onClearBookingTestResult}
                onUpdateBookingSelection={this.onUpdateBookingSelection}
                onUpdateBookings={this.onUpdateBookings}
                onRefreshBooking={this.updateBookingFromServer}
            />
          </StyledPraxSegment>
        </MainContentDiv>
    );
  }

}

export default withMessages(
    withRouterWorkaround(
        withAuthContext(
            withMandateContext(
                withPatientCentralContext(
                    withPatientCentralRefDataContext(
                        withNotificationSystemContext(PatientEntryView)))))));
